/**
 * Hook qui expose les rôles actuellement « actifs » (= un jeton est présent
 * dans localStorage pour ce rôle).
 *
 * Utilisé par le header dans `App.jsx` pour conditionner l'affichage des
 * liens de navigation : un admin voit tout, un bénévole scanner ne voit que
 * `/scan`, un bénévole caisse ne voit que `/saisie`.
 *
 * Synchronisation :
 *   - `storage` event natif (cross-tab) → si tu te déconnectes dans un autre
 *     onglet, ton onglet courant se met à jour
 *   - `focus` (revient sur l'onglet) → couvre les cas où l'onglet revient au
 *     premier plan après avoir manipulé le storage ailleurs
 *   - événement custom `sqseverac:roles` → émis manuellement par les pages
 *     /admin, /scan, /saisie après leurs login/logout pour rafraîchir le
 *     header immédiatement (sans changement de route)
 *   - changement de pathname → ceinture + bretelles pour le rafraîchissement
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Clés exactes utilisées par les 3 pages. Doivent rester synchronisées avec
// celles déclarées en haut de Admin.jsx, Scan.jsx et Saisie.jsx.
export const STORAGE_KEYS = {
  admin:  'sqseverac.admin.token',
  scan:   'sqseverac.volunteerToken',
  saisie: 'sqseverac.saisieToken',
  volunteerSession: 'sqseverac.volunteer.session',
  volunteerMeta: 'sqseverac.volunteer.meta',
};

/** Émis après chaque login/logout de jeton (même onglet). */
export const ROLES_CHANGED_EVENT = 'sqseverac:roles';

/** Jeton effectif pour `/scan` : bénévole dédié, sinon jeton admin (accepté par l’API). */
export function getEffectiveScanToken() {
  if (typeof window === 'undefined') return '';
  const v =
    localStorage.getItem(STORAGE_KEYS.scan) || localStorage.getItem(STORAGE_KEYS.admin) || '';
  return v.trim();
}

/** Jeton effectif pour `/saisie` : caisse dédiée, sinon jeton admin. */
export function getEffectiveSaisieToken() {
  if (typeof window === 'undefined') return '';
  const v =
    localStorage.getItem(STORAGE_KEYS.saisie) || localStorage.getItem(STORAGE_KEYS.admin) || '';
  return v.trim();
}

export function getVolunteerSession() {
  if (typeof window === 'undefined') return '';
  return (localStorage.getItem(STORAGE_KEYS.volunteerSession) || '').trim();
}

export function getVolunteerMeta() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.volunteerMeta);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** En-têtes auth caisse : jeton legacy ou session bénévole avec staff_access. */
export function getSaisieAuthHeaders() {
  const token = getEffectiveSaisieToken();
  if (token) return { 'X-Saisie-Token': token };
  const session = getVolunteerSession();
  const meta = getVolunteerMeta();
  if (session && (meta?.staff_role === 'saisie' || meta?.staff_role === 'admin' || meta?.is_super_admin)) {
    return { 'X-Volunteer-Session': session };
  }
  return {};
}

/** En-têtes auth admin : jeton legacy ou session super-admin / staff admin. */
export function getAdminAuthHeaders() {
  const token = typeof window !== 'undefined'
    ? (localStorage.getItem(STORAGE_KEYS.admin) || '').trim()
    : '';
  if (token) return { 'X-Admin-Token': token };
  const session = getVolunteerSession();
  const meta = getVolunteerMeta();
  if (session && (meta?.staff_role === 'admin' || meta?.is_super_admin)) {
    return { 'X-Volunteer-Session': session };
  }
  return {};
}

export function saveVolunteerSession({ sessionToken, volunteer, staffRole, isSuperAdmin }) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEYS.volunteerSession, sessionToken);
  localStorage.setItem(
    STORAGE_KEYS.volunteerMeta,
    JSON.stringify({
      staff_role: staffRole || null,
      is_super_admin: Boolean(isSuperAdmin),
      first_name: volunteer?.first_name || '',
      last_name: volunteer?.last_name || '',
      email: volunteer?.email || '',
    }),
  );
  notifyRolesChanged();
}

export function clearVolunteerSession() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEYS.volunteerSession);
  localStorage.removeItem(STORAGE_KEYS.volunteerMeta);
  notifyRolesChanged();
}

/**
 * Rafraîchit staff_role / is_super_admin depuis l’API (droits changés par le
 * super-admin sans renvoyer un lien).
 */
export function updateVolunteerMetaFromMe(me) {
  const session = getVolunteerSession();
  if (!session || !me) return;
  saveVolunteerSession({
    sessionToken: session,
    volunteer: me.volunteer,
    staffRole: me.staff_role ?? me.volunteer?.staff_role,
    isSuperAdmin: me.is_super_admin,
  });
}

function readRoles() {
  if (typeof window === 'undefined') {
    return { admin: false, scan: false, saisie: false, volunteer: false, superAdmin: false };
  }
  const meta = getVolunteerMeta();
  const hasSession = Boolean(getVolunteerSession());
  const staffAdmin = meta?.staff_role === 'admin' || meta?.is_super_admin;
  const staffSaisie = meta?.staff_role === 'saisie' || staffAdmin;
  return {
    admin:  Boolean(localStorage.getItem(STORAGE_KEYS.admin)) || (hasSession && staffAdmin),
    scan:   Boolean(localStorage.getItem(STORAGE_KEYS.scan)),
    saisie: Boolean(localStorage.getItem(STORAGE_KEYS.saisie)) || (hasSession && staffSaisie),
    volunteer: hasSession,
    superAdmin: Boolean(meta?.is_super_admin) || Boolean(localStorage.getItem(STORAGE_KEYS.admin)),
  };
}

export function useActiveRoles() {
  const [roles, setRoles] = useState(readRoles);
  const location = useLocation();
  const refresh = useCallback(() => setRoles(readRoles()), []);

  useEffect(() => { refresh(); }, [location.pathname, refresh]);

  useEffect(() => {
    window.addEventListener('storage', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener(ROLES_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(ROLES_CHANGED_EVENT, refresh);
    };
  }, [refresh]);

  return roles;
}

/**
 * À appeler explicitement après chaque mutation du localStorage liée à un
 * jeton de rôle (login/logout sur /admin, /scan, /saisie). L'event natif
 * `storage` ne se déclenche pas dans l'onglet qui a fait l'écriture, d'où
 * la nécessité de ce signal custom pour le rafraîchissement immédiat du
 * header de l'onglet courant.
 */
export function notifyRolesChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ROLES_CHANGED_EVENT));
  }
}
